import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';

import type { Step } from '@sorare/core/src/components/StepperDialog';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType } from '@sorare/core/src/lib/gql';

import { ChooseYourRewardStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/index.tsx';
import { ClaimYourRewardsStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ClaimYourRewardsStory/index.tsx';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type { DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask;
  onClose: () => void;
};
export const DecisivePlayerPickerTaskClaimStory = ({
  task,
  onClose,
}: Props) => {
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  const isDecisivePlayerPickerTask = isType(task, 'DecisivePlayerPickerTask');
  if (!isDecisivePlayerPickerTask) {
    return null;
  }

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  const normalizedTaskAppearances = Array.from(
    {
      length: task.maxAppearancesCount,
    },
    (_, index) => {
      return task.taskAppearances[index];
    }
  );

  return (
    <StepperDialog
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      steps={[
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="decisive-player-picker-story">
              <ClaimYourRewardsStory
                taskAppearances={normalizedTaskAppearances}
                mode={task.mode}
                onContinue={async () => {
                  if (!canChooseAlternativeRewardConfigs) {
                    await claimTask({
                      variables: {
                        taskId: task.id,
                        sport,
                      },
                    });
                  }
                  onNext();
                }}
              />
            </Fragment>
          ),
        },
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as Step,
            ]
          : []),
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory
                onClose={onNext}
                rewardConfigs={
                  signedAlternativeRewardConfigs
                    ? alternativeRewardConfigs[0].rewardConfigs
                    : rewardConfigs
                }
              />
            </Fragment>
          ),
        },
      ]}
    />
  );
};

DecisivePlayerPickerTaskClaimStory.fragments = {
  taskInterface: gql`
    fragment DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      mode
      maxAppearancesCount
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
      ...ClaimYourRewardsStory_taskInterface
    }
    ${ClaimYourRewardsStory.fragments.taskInterface}
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask>,
};
